import {
  Button,
  Container,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowRight,
  IconCheck,
  IconClockHour4,
  IconFileText,
  IconFlask2,
  IconLeaf,
  IconMessageCircle,
  IconMicroscope,
  IconNotebook,
  IconRosetteDiscountCheck,
  IconShieldCheck,
  IconStarFilled,
  IconUserCheck,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { buildWhatsAppUrl } from "../../../Services/contact.service";
import {
  getAllReviews,
  getReviewsByAssignmentSlug,
  getReviewSummary,
} from "../../../Services/review.service";
import { getAssignmentPageBySlug } from "../../../data/assignmentPages";
import Seo from "../../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../../seo/utils";
import { getAssignmentPagePath } from "../../../utils/routes";
import AssignmentFAQ from "../AssignmentFAQ";
import Breadcrumbs from "../Breadcrumbs";
import GetHelpForm from "../../Shared/GetHelpForm";
import ReviewsPreviewSection from "../../Reviews/ReviewsPreviewSection";
import "../BotanyAssignmentPage2.css";

const pageSlug = "customer-relationship-management-assignment-help";

const trustSignals = [
  {
    label: "CRM Assignment Help",
    score: "4.9",
    icon: IconShieldCheck,
  },
  {
    label: "Student Rated Support",
    score: "4.8",
    icon: IconUserCheck,
  },
  {
    label: "Verified Review Signals",
    score: "4.9",
    icon: IconRosetteDiscountCheck,
  },
];

const scholarCards = [
  {
    name: "Alicia Warren",
    degree: "MBA in Marketing Strategy",
    initials: "AW",
    description:
      "Focuses on customer loyalty, CRM strategy, and relationship marketing. She helps students connect theory with realistic business recommendations.",
    completedOrders: 126,
    studentReviews: 48,
    stars: 5,
    accent: "botany-v2-avatar-one",
  },
  {
    name: "Marcus Hill",
    degree: "MSc in Digital Marketing Analytics",
    initials: "MH",
    description:
      "Best for CRM reports that involve segmentation, customer analytics, journey mapping, and software-led implementation analysis.",
    completedOrders: 109,
    studentReviews: 42,
    stars: 4,
    accent: "botany-v2-avatar-two",
  },
  {
    name: "Priya Bennett",
    degree: "Ph.D. in Customer Experience",
    initials: "PB",
    description:
      "Supports students with customer experience, satisfaction, SERVQUAL, and case study analysis where the recommendations need stronger logic.",
    completedOrders: 134,
    studentReviews: 57,
    stars: 5,
    accent: "botany-v2-avatar-three",
  },
  {
    name: "Daniel Frost",
    degree: "MBA in CRM and Sales Systems",
    initials: "DF",
    description:
      "Helps with CRM implementation plans, platform analysis, and organisation-specific coursework that needs clearer structure and stronger evidence use.",
    completedOrders: 98,
    studentReviews: 36,
    stars: 4,
    accent: "botany-v2-avatar-four",
  },
];

const processSteps = [
  {
    title: "Submit Your Requirements",
    description:
      "Send us your customer relationship management assignment prompt, deadline details, and any notes your professor has already shared.",
    icon: IconNotebook,
  },
  {
    title: "Secure Payment",
    description:
      "Our experts connect with you for order pricing discussion, Pay advance 10-30% to get your work started and the rest after work completion.",
    icon: IconShieldCheck,
  },
  {
    title: "Receive Your Order",
    description:
      "Get your finished customer relationship management work before the due date with clear structure, better explanation, and personal tutor support.",
    icon: IconFileText,
  },
];

const featurePillars = [
  {
    title: "Original Writing",
    description:
      "We build every customer relationship management response around the exact task so the structure and explanation feel specific instead of generic.",
    icon: IconShieldCheck,
  },
  {
    title: "Day and Night Support",
    description:
      "You can share your brief, ask follow-up questions, and get direction whenever the assignment starts feeling unclear.",
    icon: IconMicroscope,
  },
  {
    title: "Accurate Subject Data",
    description:
      "Customer relationship management details, terminology, and evidence are checked carefully so the work reads more confidently and correctly.",
    icon: IconNotebook,
  },
  {
    title: "Correct Citations",
    description:
      "Useful for reports and essays that lose marks because references, in-text citations, or formatting feel inconsistent.",
    icon: IconClockHour4,
  },
  {
    title: "Fast Delivery",
    description:
      "When deadlines are close, we focus on the changes that improve clarity and quality fastest without wasting effort.",
    icon: IconFlask2,
  },
  {
    title: "Free Changes",
    description:
      "If a section still needs improvement after review, you can refine it further instead of feeling locked into one draft path.",
    icon: IconLeaf,
  },
];

const reviewCards = [
  {
    title: "Retail",
    meta: "CRM case study, university student",
    quote:
      "The framework explanation made the whole case study easier to understand. My recommendations finally matched the company problem instead of sounding generic.",
    date: "14 Oct 2025",
  },
  {
    title: "Hospitality",
    meta: "Customer experience report, university student",
    quote:
      "I was stuck between theory and real examples. The support helped me connect both and write a report that felt much more business-focused.",
    date: "06 Oct 2025",
  },
  {
    title: "Marketing",
    meta: "CRM strategy draft review, university student",
    quote:
      "Clear comments, stronger paragraph flow, and much better use of CRM models. The final draft felt more analytical and less descriptive.",
    date: "28 Sep 2025",
  },
];

const serviceTopics = [
  "Customer loyalty and retention analysis",
  "Customer segmentation and customer value tasks",
  "Customer journey mapping and touchpoint analysis",
  "Relationship marketing coursework support",
  "CRM value chain and IDIC model application",
  "Customer experience and satisfaction reports",
  "CRM strategy and implementation planning",
  "Salesforce, HubSpot, and platform analysis",
  "CRM case studies for retail, banking, SaaS, and hospitality",
  "Referencing and final draft improvement",
];

const freeSupportItems = [
  "Framework selection guidance",
  "Case-study structure notes",
  "Recommendation review",
  "Reference checks",
  "Presentation flow support",
  "In-text citation cleanup",
];
const contentSections = [
  {
    title: "Why Customer Relationship Management Assignment Support Matters",
    paragraphs: [
      "CRM assignments require more than simple definitions of software, loyalty, or customer service. Students usually need to explain how customer relationship management creates value, supports retention, improves experience, and influences long-term business performance.",
      "Specialised customer relationship management assignment help makes it easier to connect theory with real organisations. Whether the task is about customer lifecycle management, customer analytics, segmentation, loyalty, or CRM strategy, focused guidance can help turn a rough draft into a clearer business argument.",
    ],
    bullets: [
      "Clearer explanation of CRM concepts and frameworks",
      "Better connection between theory, business context, and evidence",
      "More organised structure for reports, essays, and case studies",
    ],
  },
  {
    title: "Common Challenges in CRM Assignments",
    paragraphs: [
      "Many students struggle with CRM assignments because the subject sits between marketing theory, customer behaviour, digital systems, data, and strategy. A single task may ask for customer retention analysis, framework use, service quality discussion, and practical recommendations in the same report.",
      "Another common challenge is moving beyond description. Students may know what CRM means but still struggle to explain how a model fits a real company, how a customer problem should be analysed, or how recommendations should be justified.",
    ],
    bullets: [
      "Difficulty choosing the right CRM model for the question",
      "Weak theory-to-case links in reports and case studies",
      "Generic recommendations that do not match the organisation",
    ],
  },
  {
    title: "How SubjectBuddy Helps With CRM Assignments",
    paragraphs: [
      "SubjectBuddy provides human-led CRM assignment help for students who need support with framework choice, case-study analysis, structure, recommendations, editing, and final submission preparation. The focus is to make the work clearer, more analytical, and better aligned with the brief.",
      "We help students understand the assignment question, organise the report logically, explain CRM concepts more clearly, and connect models with real customer problems. This support is useful for essays, reports, presentations, case studies, and reflective coursework.",
    ],
    bullets: [
      "Help with CRM essays, reports, case studies, and presentations",
      "Support for retention, loyalty, customer experience, strategy, and analytics topics",
      "Improved academic structure, clarity, recommendations, and referencing",
    ],
  },
  {
    title: "CRM Frameworks Students Commonly Use",
    paragraphs: [
      "CRM coursework becomes much stronger when students choose a small number of relevant frameworks instead of trying to mention every possible model. The right framework depends on the assignment question, the organisation, and the customer issue under discussion.",
      "Some assignments need the IDIC model or CRM value chain, while others are better served by customer lifecycle analysis, customer journey mapping, SERVQUAL, or customer lifetime value thinking. The strongest answers explain why the chosen model fits the problem.",
    ],
    bullets: [
      "IDIC model for customer identification and customisation",
      "CRM value chain for long-term customer value creation",
      "Customer journey and lifecycle models for retention and experience analysis",
    ],
  },
  {
    title: "How to Structure a CRM Assignment",
    paragraphs: [
      "A strong CRM assignment usually starts with the customer problem, not the theory list. Students should first identify the organisation, customer segment, relationship issue, and the business outcome the company wants to improve, such as loyalty, retention, satisfaction, or customer lifetime value.",
      "Once the context is clear, the report becomes easier to organise. The best structure usually introduces the issue, applies a relevant framework, discusses evidence from the business case, and finishes with realistic recommendations rather than vague textbook advice.",
    ],
    bullets: [
      "Start with the company, customer issue, and report aim",
      "Use theory to support analysis rather than replace it",
      "Link recommendations to implementation and measurement",
    ],
  },
  {
    title: "Customer Loyalty and Retention Assignment Help",
    paragraphs: [
      "Customer loyalty and retention are among the most common themes in CRM assignments. Students may be asked to explain why customers stay, why they leave, how switching costs work, how service quality affects repeat purchase, or how loyalty programmes influence long-term value.",
      "The difficulty is often not defining the concepts but analysing them properly. Strong assignments explain the drivers of loyalty, connect them to the business context, and show how CRM decisions improve relationship outcomes over time.",
    ],
    bullets: [
      "Support with loyalty, retention, and customer lifetime value analysis",
      "Clearer explanation of relationship marketing and repeat purchase behaviour",
      "Stronger recommendations linked to customer outcomes",
    ],
  },
  {
    title: "Customer Experience and Journey Mapping Support",
    paragraphs: [
      "Customer experience assignments often ask students to look at touchpoints, pain points, expectations, and service consistency across the journey. These tasks can become confusing when students list touchpoints without explaining why they matter for satisfaction, retention, or brand trust.",
      "A stronger answer shows how different stages of the journey affect customer perception and how CRM strategy can reduce friction, improve responsiveness, and build longer-term value.",
    ],
    bullets: [
      "Help with touchpoint analysis and customer journey mapping",
      "Support for customer experience and service-quality discussions",
      "Clearer links between journey problems and CRM improvements",
    ],
  },
  {
    title: "CRM Software and Implementation Analysis",
    paragraphs: [
      "Many CRM assignments mention systems such as Salesforce, HubSpot, Zoho CRM, or organisation-specific platforms. Students often lose marks when they describe software features without explaining how those features support customer strategy, communication, or decision-making.",
      "Implementation analysis becomes stronger when students discuss fit, cost, user adoption, data integration, change management, customer insight, and measurable business outcomes instead of treating CRM as only a technical installation.",
    ],
    bullets: [
      "Support for software analysis beyond feature description",
      "Stronger implementation discussion with business context",
      "Clearer links between tools, data, and customer value",
    ],
  },
  {
    title: "Common CRM Assignment Mistakes to Avoid",
    paragraphs: [
      "Many CRM assignments lose marks because they describe software, marketing activity, or customer service without explaining the deeper relationship strategy. Others use models that do not fit the case or present recommendations that feel too general to be useful.",
      "Another common problem is weak evidence use. Students may mention loyalty, satisfaction, or customer value without supporting those claims through research, business examples, or case details that match the brief.",
    ],
    bullets: [
      "Do not treat CRM as only a software installation issue",
      "Avoid generic recommendations that ignore the company context",
      "Check that every section answers the rubric and uses evidence well",
    ],
  },
  {
    title: "Why Students Choose SubjectBuddy for CRM Assignment Help",
    paragraphs: [
      "Students choose SubjectBuddy for customer relationship management assignment help because the guidance stays practical, structured, and focused on the brief. The aim is to help students understand the topic, choose stronger frameworks, and build more convincing analysis.",
      "Whether you are working on customer loyalty, relationship marketing, CRM strategy, customer experience, or platform analysis, we help improve the content, the argument flow, and the overall submission quality before the deadline.",
    ],
    bullets: [
      "Clearer structure, stronger framework selection, and better recommendations",
      "More confident theory-to-case application across reports and case studies",
      "Practical academic guidance before final submission",
    ],
  },
];

const sampleSection = {
  title: "Sample Academic Direction for a CRM Assignment",
  heading: "How the IDIC model can improve customer retention in ecommerce",
  copy: "A strong CRM assignment sample should not simply define the IDIC model and move on. It should explain how each stage of the framework supports a better customer relationship strategy inside a real organisation. For example, an ecommerce brand can identify customers through purchase history and browsing behaviour, differentiate them by value and retention risk, interact through targeted communication, and customise offers based on segment needs. A stronger answer would then evaluate whether this approach actually improves retention, loyalty, and customer lifetime value, while also recognising risks such as poor data quality, overpersonalisation, or weak cross-channel coordination. This kind of answer reads more analytically because it connects CRM theory, business evidence, and implementation logic in one clear academic discussion.",
};

const crmFaqs = [
  {
    question:
      "Can I get help with a customer relationship management case study, report, or coursework task?",
    answer:
      "Yes. SubjectBuddy can help you understand the brief, identify the key CRM issues, choose suitable frameworks, organise the structure, and improve how the analysis is presented.",
  },
  {
    question: "Which CRM topics are commonly covered in university assignments?",
    answer:
      "Common CRM topics include customer loyalty, retention, segmentation, customer experience, relationship marketing, customer lifecycle management, CRM software, customer journey mapping, CRM strategy, and implementation challenges.",
  },
  {
    question: "Can you help with CRM frameworks and models?",
    answer:
      "Yes. Support can include frameworks such as IDIC, CRM value chain, customer lifecycle, SERVQUAL, customer journey mapping, satisfaction models, and relationship marketing theories.",
  },
  {
    question: "Can you help me improve a CRM assignment draft?",
    answer:
      "Yes. Support can include structure review, paragraph flow, source use, theory-to-case connection, recommendation quality, citation checks, and clearer explanation before submission.",
  },
  {
    question:
      "Do you support urgent customer relationship management assignment help?",
    answer:
      "Yes, depending on the deadline and complexity. Share your brief, rubric, word count, deadline, and current progress so the support can focus on the highest-impact sections first.",
  },
  {
    question: "What should I share before asking for CRM assignment help?",
    answer:
      "Share the assignment brief, marking rubric, module name, deadline, word count, referencing style, case company if provided, and any draft or notes you already have.",
  },
];

function CustomerRelationshipManagementAssignmentPage() {
  const theme = useMantineTheme();
  const desktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { hash } = useLocation();
  const page = getAssignmentPageBySlug(pageSlug);

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(hash.replace(/^#/, ""));

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });

      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [hash]);

  if (!page) {
    return null;
  }

  const whatsappUrl = buildWhatsAppUrl(
    "Hi, I need help with my CRM assignment.",
  );
  const reviewSummary = getReviewSummary();
  const relatedReviews = getReviewsByAssignmentSlug(page.slug, 3);
  const fallbackReviews = getAllReviews().slice(0, 3);
  const orderPagePath = `/order-form?from=${page.slug}`;

  const seoTitle =
    page.metaTitle || "CRM Assignment Help | Customer Relationship Management";
  const seoDescription =
    page.metaDescription ||
    "Get customer relationship management assignment help for CRM reports, case studies, loyalty, retention, customer experience, CRM strategy, and software analysis.";

  return (
    <main className="botany-v2-page">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={getAssignmentPagePath(page.slug)}
        schema={[
          createOrganizationSchema(),
          createFaqSchema(crmFaqs),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: page.title, path: getAssignmentPagePath(page.slug) },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.title,
            provider: {
              "@type": "Organization",
              name: process.env.REACT_APP_NAME || "SubjectBuddy",
            },
            serviceType: "Customer relationship management assignment help",
            areaServed: [
              "United States",
              "United Kingdom",
              "Canada",
              "Australia",
            ],
            description: seoDescription,
          },
        ]}
      />

      <Container size="xl" className="botany-v2-shell">
        <section className="botany-v2-hero">
          <Breadcrumbs
            items={[{ label: "Home", href: "/" }, { label: page.title }]}
          />

          <div className="botany-v2-hero-grid">
            <div className="botany-v2-hero-copy">
              <Title order={1} className="botany-v2-title">
                Need Customer Relationship Management Assignment Help?
              </Title>
              <Text className="botany-v2-subtitle">
                SubjectBuddy provides human-led CRM assignment help for students
                working on reports, case studies, essays, presentations, and
                coursework. We help you understand loyalty, retention,
                segmentation, customer experience, CRM strategy, and platform
                analysis so the final assignment feels clearer and more
                convincing.
              </Text>

              <div className="botany-v2-trust">
                Trusted by <strong>{reviewSummary.totalReviews}+</strong>{" "}
                students seeking clearer assignment support
              </div>

              <div className="botany-v2-trust-row">
                {trustSignals.map((item) => (
                  <div key={item.label} className="botany-v2-trust-card">
                    <div className="botany-v2-trust-head">
                      <ThemeIcon
                        size={34}
                        radius="xl"
                        color="orange"
                        variant="light"
                      >
                        <item.icon size={18} />
                      </ThemeIcon>
                      <div>
                        <div className="botany-v2-trust-label">
                          {item.label}
                        </div>
                        <div className="botany-v2-stars">
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <span>{item.score}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="botany-v2-form-shell">
              <Paper
                className="botany-v2-form-card"
                radius="xl"
                p={desktop ? "1.5rem" : "1rem"}
              >
                <Stack gap="md">
                  <Title order={3} ta="center" className="botany-v2-form-title">
                    Struggling with your CRM assignment?
                  </Title>
                  <div className="botany-v2-form-badges">
                    <span>
                      <IconCheck size={14} /> Better framework choice
                    </span>
                    <span>
                      <IconCheck size={14} /> Stronger case analysis
                    </span>
                    <span>
                      <IconCheck size={14} /> Faster turnaround
                    </span>
                  </div>
                  <GetHelpForm
                    formSize="sm"
                    flatForm
                    headerMode="minimal"
                    submitLabel="Get CRM Help"
                  />
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-experts">
          <div className="botany-v2-experts-shell">
            <div className="botany-v2-section-heading botany-v2-section-heading-center">
              <Title order={2}>
                Meet the Experts Supporting CRM Assignment Help
              </Title>
              <Text>
                Our team includes business and marketing specialists who know
                how to connect CRM theory with customer issues, company cases,
                and more realistic recommendations.
              </Text>
            </div>

            <SimpleGrid cols={{ base: 1, md: 2, xl: 4 }} spacing="lg">
              {scholarCards.map((card) => (
                <Paper
                  key={card.name}
                  className="botany-v2-scholar-card"
                  radius="xl"
                  p="lg"
                >
                  <Stack gap="md" h="100%">
                    <Group align="flex-start" wrap="nowrap">
                      <div className={`botany-v2-avatar ${card.accent}`}>
                        <span>{card.initials}</span>
                        <span className="botany-v2-avatar-status" />
                      </div>
                      <div>
                        <Text fw={900}>{card.name}</Text>
                        <Text size="sm" c="dimmed">
                          {card.degree}
                        </Text>
                        <div className="botany-v2-scholar-stars">
                          {Array.from({ length: 5 }).map((_, index) => (
                            <IconStarFilled
                              key={`${card.name}-${index}`}
                              size={14}
                              className={
                                index < card.stars
                                  ? "botany-v2-star-on"
                                  : "botany-v2-star-off"
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </Group>

                    <Text size="sm" style={{ lineHeight: 1.8 }}>
                      {card.description}
                    </Text>

                    <div className="botany-v2-scholar-footer">
                      <div className="botany-v2-scholar-stats">
                        <span>
                          <IconFileText size={13} />
                          <strong>{card.completedOrders}</strong> Completed
                          Orders
                        </span>
                        <span>
                          <IconMessageCircle size={13} />
                          <strong>{card.studentReviews}</strong> Student Reviews
                        </span>
                      </div>

                      <Button
                        component="a"
                        href={whatsappUrl}
                        radius="md"
                        variant="outline"
                        color="green"
                      >
                        Get Help
                      </Button>
                    </div>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </div>
        </section>

        <section className="botany-v2-process">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              From Seed to Success: How to Secure Your Professional Customer
              Relationship Management Assignment
            </Title>
          </div>

          <div className="botany-v2-process-flow">
            {processSteps.map((step, index) => (
              <div key={step.title} className="botany-v2-process-node">
                <div className="botany-v2-process-item">
                  <div className="botany-v2-process-icon-shell">
                    <step.icon size={56} stroke={1.5} />
                  </div>
                  <Title order={3}>{step.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {step.description}
                  </Text>
                </div>

                {index < processSteps.length - 1 ? (
                  <div className="botany-v2-process-arrow" aria-hidden="true">
                    <IconArrowRight size={44} stroke={2.2} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="botany-v2-process-cta">
            <Button
              component={Link}
              to={orderPagePath}
              color="green"
              radius="md"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-features">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              Why We Provide the Best Customer Relationship Management
              Assignment Help in the United States
            </Title>
            <Text>
              We make sure your assignment work is done correctly. We focus on
              quality, speed, and facts.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="xl">
            {featurePillars.map((item) => (
              <div key={item.title} className="botany-v2-feature-row">
                <ThemeIcon
                  size={64}
                  radius="xl"
                  color="green"
                  variant="light"
                  className="botany-v2-feature-icon"
                >
                  <item.icon size={28} />
                </ThemeIcon>
                <Stack gap={6} className="botany-v2-feature-copy">
                  <Title order={3}>{item.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {item.description}
                  </Text>
                </Stack>
              </div>
            ))}
          </SimpleGrid>

          <div className="botany-v2-feature-cta">
            <Button
              component={Link}
              to={orderPagePath}
              radius="md"
              color="green"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-reviews">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              What Students Say About Our CRM Assignment Help
            </Title>
            <Text>
              Reviews from students who wanted clearer case analysis, better
              framework selection, and stronger final submissions.
            </Text>
          </div>

          <div className="botany-v2-review-platforms">
            {trustSignals.map((item) => (
              <div key={item.label} className="botany-v2-platform-pill">
                <item.icon size={16} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {reviewCards.map((review) => (
              <Paper
                key={review.title}
                className="botany-v2-review-card"
                radius="lg"
                p="lg"
              >
                <Stack gap="sm">
                  <div className="botany-v2-stars">
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                  </div>
                  <Text fw={800}>{review.title}</Text>
                  <Text size="xs" c="dimmed">
                    {review.meta}
                  </Text>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.8 }}>
                    {review.quote}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {review.date}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </section>

        <section className="botany-v2-urgent">
          <Paper
            className="botany-v2-urgent-card"
            radius="xl"
            p={desktop ? "2rem" : "1.25rem"}
          >
            <Grid align="center" gutter="xl">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <div className="botany-v2-urgent-visual">
                  <IconNotebook size={84} />
                  <IconMessageCircle
                    size={34}
                    className="botany-v2-float botany-v2-float-one"
                  />
                  <IconFileText
                    size={30}
                    className="botany-v2-float botany-v2-float-two"
                  />
                  <IconShieldCheck
                    size={30}
                    className="botany-v2-float botany-v2-float-three"
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <Title order={2}>
                    Get Urgent CRM Assignment Help for Tight Deadlines
                  </Title>
                  <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                    Access urgent support for CRM reports, customer experience
                    analysis, case studies, and draft improvement when the
                    deadline is close and the next steps need to become clear
                    quickly.
                  </Text>
                  <Button
                    component={Link}
                    to={orderPagePath}
                    radius="md"
                    color="green"
                    leftSection={<IconClockHour4 size={18} />}
                    className="botany-v2-urgent-button"
                  >
                    Order Support
                  </Button>
                </Stack>
              </Grid.Col>
            </Grid>
          </Paper>
        </section>

        <section className="botany-v2-services">
          <div className="botany-v2-services-grid">
            <div className="botany-v2-services-left">
              <Title order={2}>
                Our CRM Assignment Support Covers Every Academic Need
              </Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                SubjectBuddy supports CRM reports, case studies, essays,
                presentations, and implementation tasks that need clearer
                structure, stronger customer analysis, and more confident
                academic flow.
              </Text>
              <Text fw={800}>Type of support</Text>
              <ul className="botany-v2-service-list">
                {serviceTopics.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="botany-v2-services-right">
              <Paper className="botany-v2-freebies-card" radius="xl" p="lg">
                <Stack gap="md">
                  <Text fw={900} tt="uppercase" size="sm" c="orange">
                    Included support areas
                  </Text>
                  {freeSupportItems.map((item) => (
                    <div key={item} className="botany-v2-freebie-row">
                      <span className="botany-v2-freebie-label">
                        <IconCheck size={16} />
                        {item}
                      </span>
                      <span className="botany-v2-freebie-value">Included</span>
                    </div>
                  ))}
                  <Button
                    component="a"
                    href={whatsappUrl}
                    radius="md"
                    color="orange"
                  >
                    Get Support
                  </Button>
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-content">
          <div className="botany-v2-content-scroll">
            <div className="botany-v2-content-grid">
              {contentSections.map((section) => (
                <article key={section.title} className="botany-v2-article-card">
                  <Stack gap="md">
                    <Title order={2}>{section.title}</Title>
                    {section.paragraphs.map((paragraph) => (
                      <Text
                        key={paragraph}
                        c="dimmed"
                        style={{ lineHeight: 1.9 }}
                      >
                        {paragraph}
                      </Text>
                    ))}
                    <ul className="botany-v2-article-list">
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </Stack>
                </article>
              ))}

              <article className="botany-v2-article-card">
                <Stack gap="md">
                  <Title order={2}>{sampleSection.title}</Title>
                  <Paper className="botany-v2-sample-card" radius="lg" p="lg">
                    <Stack gap="sm">
                      <Title order={3}>{sampleSection.heading}</Title>
                      <Text c="dimmed" style={{ lineHeight: 1.85 }}>
                        {sampleSection.copy}
                      </Text>
                    </Stack>
                  </Paper>
                </Stack>
              </article>
            </div>
          </div>
        </section>

        <section className="botany-v2-live-reviews">
          <ReviewsPreviewSection
            title="Recent Student Feedback on CRM Support"
            description="Selected student feedback that reflects clearer framework use, stronger business reasoning, and calmer deadline support."
            reviews={relatedReviews.length ? relatedReviews : fallbackReviews}
            compactCards
            plainTrustBar
          />
        </section>

        <section className="botany-v2-faqs">
          <div className="botany-v2-section-heading">
            <Title order={2}>FAQs on CRM Assignment Help</Title>
          </div>
          <AssignmentFAQ faqs={crmFaqs} />
        </section>
      </Container>
    </main>
  );
}

export default CustomerRelationshipManagementAssignmentPage;
